import { useEffect, useRef, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import Text from "../../../shared/ui/Typography/Text";

import {
  createClientViewing,
  fetchViewingPropertyOptions,
} from "../api/viewings";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientCreateViewingModal — feature-local T-3 modal for scheduling
// a property viewing against the current client (CRM-WORKFLOW-C1).

const EDITABLE_KEYS = ["property", "scheduled_at", "notes", "client_id"];

const INITIAL_DRAFT = {
  propertyId: "",
  scheduled_at: "",
  notes: "",
};

const scheduledAtForApi = (value) => {
  if (typeof value !== "string" || value.trim().length === 0) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toISOString();
};

const unwrapPropertyResults = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
};

const formatPrice = (amount, currency) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return "";
  const formatted = numeric.toLocaleString("uk-UA", {
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

const locationSummary = (property) => {
  const loc = property && property.location;
  if (!loc) return "";
  if (loc.formatted_address) return loc.formatted_address;
  if (loc.settlement_name) return loc.settlement_name;
  if (loc.settlement_unit && loc.settlement_unit.name) return loc.settlement_unit.name;
  if (loc.region_name) return loc.region_name;
  return "";
};

function ClientCreateViewingModal({ open, onClose, clientId, onSaved }) {
  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitState, setSubmitState] = useState("idle");
  const submittingRef = useRef(false);

  const [propertySearch, setPropertySearch] = useState("");
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoadState, setPropertyLoadState] = useState("idle");
  const [propertyLoadError, setPropertyLoadError] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(INITIAL_DRAFT);
      setFieldErrors({});
      setFormError(null);
      setSubmitState("idle");
      submittingRef.current = false;
      setPropertySearch("");
      setPropertyOptions([]);
      setPropertyLoadState("loading");
      setPropertyLoadError(null);

      let cancelled = false;
      fetchViewingPropertyOptions()
        .then((data) => {
          if (cancelled) return;
          setPropertyOptions(unwrapPropertyResults(data));
          setPropertyLoadState("loaded");
        })
        .catch((err) => {
          if (cancelled) return;
          setPropertyLoadError(messageFromError(err));
          setPropertyLoadState("error");
        });

      return () => {
        cancelled = true;
      };
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        if (submittingRef.current) return;
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const submitting = submitState === "submitting";

  const requestClose = () => {
    if (submittingRef.current) return;
    onClose();
  };

  const handleFieldChange = (field) => (event) => {
    const next = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: next }));
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const handlePropertySelect = (propertyId) => {
    setDraft((prev) => ({ ...prev, propertyId: String(propertyId) }));
    setFieldErrors((prev) => {
      if (!prev.property) return prev;
      const out = { ...prev };
      delete out.property;
      return out;
    });
    setFormError(null);
  };

  const handlePropertySearch = async (event) => {
    if (event) event.preventDefault();
    setPropertyLoadState("loading");
    setPropertyLoadError(null);
    try {
      const params = {};
      const query = propertySearch.trim();
      if (query.length > 0) {
        params.search = query;
      }
      const data = await fetchViewingPropertyOptions(params);
      setPropertyOptions(unwrapPropertyResults(data));
      setPropertyLoadState("loaded");
    } catch (err) {
      setPropertyLoadError(messageFromError(err));
      setPropertyLoadState("error");
    }
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting || submittingRef.current) return;

    if (!draft.propertyId) {
      setFieldErrors({ property: "Оберіть об'єкт нерухомості." });
      return;
    }
    if (!draft.scheduled_at) {
      setFieldErrors({ scheduled_at: "Вкажіть дату та час перегляду." });
      return;
    }

    submittingRef.current = true;
    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);

    const body = {
      client_id: clientId,
      property: Number(draft.propertyId),
      scheduled_at: scheduledAtForApi(draft.scheduled_at),
      notes: draft.notes ?? "",
    };

    try {
      await createClientViewing(body);
      setSubmitState("saved");
      submittingRef.current = false;
      onSaved();
    } catch (err) {
      submittingRef.current = false;
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(err));
      setSubmitState("error");
    }
  };

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-create-viewing-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={requestClose}
      />
      <div className="feature-client-detail__modal-dialog">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-create-viewing-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-calendar-check" aria-hidden="true" />
            <span>Запланувати перегляд</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={requestClose}
            disabled={submitting}
            aria-label="Закрити вікно планування перегляду"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <form className="ui-form" onSubmit={handleSubmit} noValidate>
            <div className="ui-form__rows">
              <Field
                id="client-create-viewing-property"
                label="Об'єкт нерухомості"
                required
                error={fieldErrors.property}
              >
                <div className="feature-client-detail__property-picker">
                  <div className="feature-client-detail__property-search">
                    <TextInput
                      name="property_search"
                      value={propertySearch}
                      onChange={(event) => setPropertySearch(event.target.value)}
                      placeholder="Пошук за назвою…"
                      disabled={submitting}
                    />
                    <button
                      type="button"
                      className="ui-form__button ui-form__button--ghost"
                      onClick={handlePropertySearch}
                      disabled={submitting || propertyLoadState === "loading"}
                    >
                      {propertyLoadState === "loading" ? (
                        <Spinner size="sm" label="Пошук" />
                      ) : (
                        "Шукати"
                      )}
                    </button>
                  </div>

                  {propertyLoadError ? (
                    <InlineError className="feature-client-detail__property-picker-error">
                      {propertyLoadError}
                    </InlineError>
                  ) : null}

                  {propertyLoadState === "loading" &&
                  propertyOptions.length === 0 ? (
                    <div className="feature-client-detail__property-picker-state">
                      <Spinner size="sm" label="Завантаження об'єктів" />
                    </div>
                  ) : propertyLoadState !== "loading" &&
                    propertyOptions.length === 0 ? (
                    <Text as="p" size="sm" tone="secondary">
                      Активних об'єктів не знайдено.
                    </Text>
                  ) : (
                    <ul
                      className="feature-client-detail__property-options"
                      role="listbox"
                      aria-label="Оберіть об'єкт"
                    >
                      {propertyOptions.map((property) => {
                        const selected =
                          String(draft.propertyId) === String(property.id);
                        const loc = locationSummary(property);
                        const price = formatPrice(
                          property.price,
                          property.currency,
                        );
                        return (
                          <li key={property.id}>
                            <button
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={
                                selected
                                  ? "feature-client-detail__property-option feature-client-detail__property-option--selected"
                                  : "feature-client-detail__property-option"
                              }
                              onClick={() => handlePropertySelect(property.id)}
                              disabled={submitting}
                            >
                              <span className="feature-client-detail__property-option-title">
                                {property.title || `Об'єкт #${property.id}`}
                              </span>
                              <span className="feature-client-detail__property-option-meta">
                                {[loc, price].filter(Boolean).join(" · ")}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </Field>

              <Field
                id="client-create-viewing-scheduled-at"
                label="Дата та час"
                required
                error={fieldErrors.scheduled_at}
              >
                <TextInput
                  type="datetime-local"
                  name="scheduled_at"
                  value={draft.scheduled_at}
                  onChange={handleFieldChange("scheduled_at")}
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-create-viewing-notes"
                label="Нотатки"
                error={fieldErrors.notes}
              >
                <Textarea
                  name="notes"
                  value={draft.notes}
                  onChange={handleFieldChange("notes")}
                  rows={3}
                  placeholder="Додаткові деталі…"
                  disabled={submitting}
                />
              </Field>
            </div>

            <div className="ui-form__messages">
              {formError ? (
                <InlineError className="ui-form__message">
                  {formError}
                </InlineError>
              ) : null}
            </div>

            <div className="ui-form__actions">
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                onClick={requestClose}
                disabled={submitting}
              >
                Скасувати
              </button>
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={submitting}
                data-loading={submitting ? "true" : undefined}
              >
                {submitting ? (
                  <Spinner size="sm" label="Створення" />
                ) : (
                  "Запланувати"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientCreateViewingModal;
